import React, { useState } from "react";
import { Container, Card, Form, Button, Table, Alert, Spinner, Badge } from "react-bootstrap";
import api from '../../services/api';
import "./ProductCardsPreview.css";

const ProductCardsPreview = () => {
  // Состояния для первого раздела
  const [articlePart, setArticlePart] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Состояния для второго раздела
  const [yandexDiskPath, setYandexDiskPath] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState("");

  // Функция для очистки строки от скрытых символов
  const cleanString = (str) => {
    return str
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Убираем управляющие символы
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // Убираем zero-width space и BOM
      .replace(/\s+/g, ' ') // Заменяем множественные пробелы на один
      .trim(); // Убираем пробелы в начале и конце
  };

  // Обработчик изменения поля ввода
  const handleArticlePartChange = (e) => {
    const value = e.target.value;
    // Очищаем от скрытых символов при вставке
    const cleanedValue = cleanString(value);
    setArticlePart(cleanedValue);
  };

  // Обработчик вставки из буфера
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const cleanedText = cleanString(pastedText);
    
    // Вставляем очищенный текст
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const newValue = articlePart.slice(0, start) + cleanedText + articlePart.slice(end);
    setArticlePart(newValue);
  };

  // Поиск карточек
  const handleSearch = async () => {
    const cleanedArticlePart = cleanString(articlePart);
    
    if (!cleanedArticlePart) {
      setSearchError("Введите часть артикула для поиска");
      return;
    }

    setIsSearching(true);
    setSearchError("");
    setSelectedCards(new Set());
    setUploadResult(null);

    try {
      const response = await api.get(`/productcards?articlePart=${encodeURIComponent(cleanedArticlePart)}`);
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        setSearchError("Ничего не найдено");
      }
    } catch (err) {
      console.error("Ошибка при поиске:", err);
      setSearchError("Ошибка при поиске. Попробуйте еще раз.");
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Управление выбором карточек
  const toggleCardSelection = (nmId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(nmId)) {
      newSelected.delete(nmId);
    } else {
      newSelected.add(nmId);
    }
    setSelectedCards(newSelected);
  };

  const toggleAllCards = () => {
    if (selectedCards.size === searchResults.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(searchResults.map(card => card.nmId)));
    }
  };

  // Загрузка превью
  const handleUpload = async () => {
    const cleanedPath = cleanString(yandexDiskPath);
    
    if (!cleanedPath) {
      setUploadError("Введите путь к папке на Яндекс.Диске");
      return;
    }

    if (selectedCards.size === 0) {
      setUploadError("Выберите хотя бы один товар");
      return;
    }

    setIsUploading(true);
    setUploadError("");
    setUploadResult(null);

    const selectedArticles = searchResults
      .filter(card => selectedCards.has(card.nmId))
      .map(card => ({
        article: card.article,
        nmId: card.nmId
      }));

    try {
      const response = await api.post("/productcards", {
        yandexDiskPath: cleanedPath,
        articles: selectedArticles
      });

      setUploadResult(response.data);
      
      // Очищаем выбор после успешной загрузки
      if (!response.data.errorMessage) {
        setSelectedCards(new Set());
        setYandexDiskPath("");
      }
    } catch (err) {
      console.error("Ошибка при загрузке превью:", err);
      setUploadError("Ошибка при загрузке превью. Попробуйте еще раз.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Container fluid className="mt-4">
      <h2 className="mb-4">Загрузка превью товаров</h2>

      {/* Первый раздел - Поиск товаров */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Шаг 1: Поиск товаров без превью</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Артикул или его часть</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={articlePart}
                onChange={handleArticlePartChange}
                onPaste={handlePaste}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Введите артикул или его часть"
                disabled={isSearching}
              />
              <Button 
                variant="primary" 
                onClick={handleSearch}
                disabled={isSearching}
              >
                {isSearching ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Поиск...
                  </>
                ) : (
                  'Найти'
                )}
              </Button>
            </div>
          </Form.Group>

          {searchError && (
            <Alert variant="warning" className="mt-3">
              {searchError}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span>Найдено товаров: <Badge bg="primary">{searchResults.length}</Badge></span>
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={toggleAllCards}
                >
                  {selectedCards.size === searchResults.length ? 'Снять выделение' : 'Выделить все'}
                </Button>
              </div>
              
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={selectedCards.size === searchResults.length && searchResults.length > 0}
                        onChange={toggleAllCards}
                      />
                    </th>
                    <th>Артикул</th>
                    <th>NmId</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((card) => (
                    <tr key={card.nmId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={selectedCards.has(card.nmId)}
                          onChange={() => toggleCardSelection(card.nmId)}
                        />
                      </td>
                      <td>{card.article}</td>
                      <td>{card.nmId}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Второй раздел - Загрузка превью */}
      <Card className={selectedCards.size === 0 ? 'opacity-50' : ''}>
        <Card.Header>
          <h5 className="mb-0">
            Шаг 2: Загрузка превью с Яндекс.Диска
            {selectedCards.size > 0 && (
              <Badge bg="success" className="ms-2">
                Выбрано: {selectedCards.size}
              </Badge>
            )}
          </h5>
        </Card.Header>
        <Card.Body>
          <fieldset disabled={selectedCards.size === 0 || isUploading}>
            <Form.Group className="mb-3">
              <Form.Label>Путь к папке на Яндекс.Диске</Form.Label>
              <Form.Control
                type="text"
                value={yandexDiskPath}
                onChange={(e) => setYandexDiskPath(cleanString(e.target.value))}
                onPaste={handlePaste}
                placeholder="Например: Сервер/Товары/Брелки/Превью"
                disabled={selectedCards.size === 0}
              />
              <Form.Text className="text-muted">
                Укажите путь к папке, содержащей подпапки с превью для каждого артикула
              </Form.Text>
            </Form.Group>

            <Button 
              variant="success" 
              onClick={handleUpload}
              disabled={selectedCards.size === 0 || isUploading || !yandexDiskPath.trim()}
            >
              {isUploading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Загрузка...
                </>
              ) : (
                'Загрузить превью'
              )}
            </Button>
          </fieldset>

          {uploadError && (
            <Alert variant="danger" className="mt-3">
              {uploadError}
            </Alert>
          )}

          {uploadResult && (
            <div className="mt-3">
              {uploadResult.successMessage && (
                <Alert variant="success">
                  <strong>Успешно:</strong>
                  <pre className="mb-0 mt-2">{uploadResult.successMessage}</pre>
                </Alert>
              )}
              {uploadResult.errorMessage && (
                <Alert variant="warning">
                  <strong>Ошибки:</strong>
                  <pre className="mb-0 mt-2">{uploadResult.errorMessage}</pre>
                </Alert>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ProductCardsPreview;